import { z } from "zod";
import { billingFeaturesSchema, billingQuotaWindowSchema } from "./billing-access.js";
export const BILLING_CURRENCY = "RUB";
export const BILLING_PERIOD_DAYS = 30;
export const BILLING_TIME_ZONE = "Europe/Moscow";
const POSTGRES_INT_MAX = 2_147_483_647;
const PERIOD_MS = 2_592_000_000;
// The API's ordinary JSON body limit is 1 MiB. This guards BigInt parsing of
// individual operands without imposing a commercial price or volume ceiling.
const MAX_DECIMAL_CHARS = 4_096;
const countSchema = z.number().int().min(0).max(POSTGRES_INT_MAX);
const positiveCountSchema = countSchema.min(1);
const versionSchema = countSchema;
const idSchema = z.string().min(1);
const nameSchema = z.string().min(1);
export const isoUtcSchema = z.string().refine((value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/.exec(value);
    if (!match || match[1] === "0000")
        return false;
    const canonical = value.replace(/(?:\.(\d{1,3}))?Z$/, (_whole, fraction) => `.${(fraction ?? "").padEnd(3, "0")}Z`);
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString() === canonical;
}, "Expected a valid UTC ISO timestamp within the JavaScript/PostgreSQL date range");
/** Canonical, finite, nonnegative decimal; no exponent or binary float conversion. */
export function normalizeDecimalString(value) {
    if (!/^\d+(?:\.\d+)?$/.test(value)) {
        throw new Error("Expected a nonnegative decimal string without an exponent");
    }
    const [integer = "", fraction = ""] = value.split(".");
    const whole = integer.replace(/^0+/, "") || "0";
    const fractional = fraction.replace(/0+$/, "");
    return fractional ? `${whole}.${fractional}` : whole;
}
const decimalOperandSchema = z.string().max(MAX_DECIMAL_CHARS).regex(/^\d+(?:\.\d+)?$/).transform(normalizeDecimalString);
// Calculated products may carry more digits than either input operand.
const nonnegativeDecimalSchema = z.string().regex(/^\d+(?:\.\d+)?$/).transform(normalizeDecimalString);
const positiveDecimalSchema = decimalOperandSchema.refine((value) => value !== "0", "Must be positive");
const discountSchema = decimalOperandSchema.refine((value) => {
    const [whole = "0", fraction] = value.split(".");
    return Number(whole) < 100 || (whole === "100" && fraction === undefined);
}, "Discount must be between 0 and 100 percent");
const integerRubSchema = z.string().regex(/^(?:0|[1-9]\d*)$/);
export const tariffIdentitySchema = z.object({
    id: idSchema,
    name: nameSchema,
    kind: z.enum(["FREEMIUM", "TRIAL", "PAID"]),
}).strict();
const tariffTermSchema = z.object({
    months: positiveCountSchema,
    discountPercent: discountSchema,
}).strict();
const paidTariffSchema = z.object({
    id: idSchema.refine((id) => id !== "freemium" && id !== "trial", "Reserved tariff ID"),
    name: nameSchema,
    monthlyDialogs: positiveCountSchema,
    unitPriceRub: positiveDecimalSchema,
    terms: z.array(tariffTermSchema).min(1),
}).strict().superRefine((tariff, context) => {
    const months = new Set();
    tariff.terms.forEach((term, index) => {
        if (months.has(term.months)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["terms", index, "months"], message: "Duplicate term" });
        }
        months.add(term.months);
    });
});
export const TRIAL_DAY_MS = 86_400_000;
export const trialSettingsSchema = z.object({
    durationDays: positiveCountSchema.max(2_000_000),
    totalDialogs: positiveCountSchema,
}).strict();
export const trialSubscriptionSnapshotSchema = trialSettingsSchema.extend({
    schemaVersion: z.literal(1), kind: z.literal("TRIAL"),
    tariffId: z.literal("trial"), tariffName: nameSchema, catalogVersion: versionSchema,
}).strict();
export const trialActivationSchema = z.object({
    subscriptionId: idSchema, snapshot: trialSubscriptionSnapshotSchema,
    startsAt: isoUtcSchema, scheduledEndsAt: isoUtcSchema, actualEndsAt: isoUtcSchema,
}).strict();
export const trialAvailabilitySchema = z.object({
    state: z.enum(["AVAILABLE", "ACTIVE", "USED", "PAID_ACTIVE", "NOT_CONFIGURED"]),
    canActivate: z.boolean(), settings: trialSettingsSchema.nullable(),
    activation: trialActivationSchema.nullable(),
}).strict();
export const trialActivateInputSchema = z.object({
    requestId: z.string().uuid(), expectedCatalogVersion: versionSchema, expectedBillingVersion: versionSchema,
}).strict();
export const tariffDefinitionSchema = z.object({
    currency: z.literal(BILLING_CURRENCY),
    periodDays: z.literal(BILLING_PERIOD_DAYS),
    freemium: z.object({
        id: z.literal("freemium"),
        name: nameSchema,
        monthlyDialogs: countSchema,
    }).strict(),
    trial: z.object({ id: z.literal("trial"), name: nameSchema, settings: trialSettingsSchema.nullable() }).strict(),
    paidTariffs: z.array(paidTariffSchema),
}).strict().superRefine((definition, context) => {
    const ids = new Set();
    definition.paidTariffs.forEach((tariff, index) => {
        if (ids.has(tariff.id)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["paidTariffs", index, "id"], message: "Duplicate tariff ID" });
        }
        ids.add(tariff.id);
    });
});
const purchaseBreakdownSchema = z.object({
    baseMonthlyRub: nonnegativeDecimalSchema,
    undiscountedRub: nonnegativeDecimalSchema,
    discountRub: nonnegativeDecimalSchema,
    totalRub: integerRubSchema,
}).strict();
export const tariffPurchaseQuoteSchema = z.object({
    catalogVersion: versionSchema,
    tariffId: idSchema,
    tariffName: nameSchema,
    monthlyDialogs: positiveCountSchema,
    months: positiveCountSchema,
    periodDays: z.literal(BILLING_PERIOD_DAYS),
    unitPriceRub: positiveDecimalSchema,
    discountPercent: discountSchema,
    breakdown: purchaseBreakdownSchema,
}).strict();
export const tariffCatalogSchema = z.object({
    version: versionSchema,
    updatedAt: isoUtcSchema,
    definition: tariffDefinitionSchema,
    baseQuotes: z.array(tariffPurchaseQuoteSchema),
}).strict().superRefine((catalog, context) => {
    catalog.baseQuotes.forEach((quote, index) => {
        if (quote.catalogVersion !== catalog.version) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ["baseQuotes", index, "catalogVersion"], message: "Quote catalog version differs" });
        }
    });
});
export const publicTariffQuoteInputSchema = z.object({
    catalogVersion: versionSchema,
    tariffId: idSchema,
    monthlyDialogs: positiveCountSchema,
    months: positiveCountSchema,
}).strict();
export const tariffCatalogPreviewInputSchema = z.object({
    expectedVersion: versionSchema,
    definition: tariffDefinitionSchema,
}).strict();
export const tariffCatalogPreviewSchema = z.object({
    expectedVersion: versionSchema,
    catalog: tariffCatalogSchema,
    diff: z.object({
        addedTariffIds: z.array(idSchema),
        removedTariffIds: z.array(idSchema),
        changedTariffIds: z.array(idSchema),
        freemiumChanged: z.boolean(),
        trialChanged: z.boolean(),
    }).strict(),
    calculatedAt: isoUtcSchema,
    expiresAt: isoUtcSchema,
    previewToken: z.string().min(1),
}).strict();
export const tariffCatalogConfirmInputSchema = z.object({
    expectedVersion: versionSchema,
    previewToken: z.string().min(1),
}).strict();
export const paidSubscriptionSnapshotSchema = z.object({
    schemaVersion: z.literal(1),
    tariffId: idSchema,
    tariffName: nameSchema,
    catalogVersion: versionSchema,
    baseMonthlyDialogs: positiveCountSchema,
    monthlyDialogs: positiveCountSchema,
    unitPriceRub: positiveDecimalSchema,
    months: positiveCountSchema,
    discountPercent: discountSchema,
    baseMonthlyRub: nonnegativeDecimalSchema,
    purchaseTotalRub: integerRubSchema,
}).strict().refine((snapshot) => snapshot.monthlyDialogs >= snapshot.baseMonthlyDialogs, {
    path: ["monthlyDialogs"], message: "Selected volume is below the base volume",
});
export const billingSummarySchema = z.object({
    quotaWindow: billingQuotaWindowSchema,
    monthlyDialogs: countSchema,
    usedDialogs: countSchema,
    topupRemaining: countSchema,
    reservedDialogs: countSchema,
    availableDialogs: countSchema,
    periodEndsAt: isoUtcSchema,
    subscriptionEndsAt: isoUtcSchema.nullable(),
}).strict();
export const billingViewSchema = z.object({
    quotaWindow: billingQuotaWindowSchema,
    trial: trialAvailabilitySchema,
    features: billingFeaturesSchema,
    accessValidUntil: isoUtcSchema.nullable(),
    resolvedAt: isoUtcSchema,
    catalogVersion: versionSchema,
    billingVersion: versionSchema,
    tariff: tariffIdentitySchema,
    subscription: z.object({
        id: idSchema,
        kind: z.enum(["FREEMIUM", "TRIAL", "PAID"]),
        startsAt: isoUtcSchema,
        endsAt: isoUtcSchema.nullable(),
        originalPaidSnapshot: paidSubscriptionSnapshotSchema.nullable(),
        effectivePaidSnapshot: paidSubscriptionSnapshotSchema.nullable(),
        trialSnapshot: trialSubscriptionSnapshotSchema.nullable(),
    }).strict(),
    period: z.object({ id: idSchema, index: countSchema, startsAt: isoUtcSchema, endsAt: isoUtcSchema }).strict(),
    monthlyDialogs: countSchema,
    usedDialogs: countSchema,
    topupGranted: countSchema,
    topupUsed: countSchema,
    topupRemaining: countSchema,
    reservedDialogs: countSchema,
    availableDialogs: countSchema,
    allowedActions: z.object({ assign: z.boolean(), topup: z.boolean(), upgrade: z.boolean() }).strict(),
}).strict();
export const adminBillingViewSchema = billingViewSchema.extend({
    pendingConfirmations: z.object({
        heldReservations: countSchema,
        unknownAttempts: countSchema,
        timeUnresolvedAttempts: countSchema,
    }).strict(),
}).strict();
export const billingActionInputSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("ASSIGN"), tariffId: idSchema, monthlyDialogs: positiveCountSchema, months: positiveCountSchema }).strict(),
    z.object({ kind: z.literal("TOPUP"), dialogs: positiveCountSchema }).strict(),
    z.object({ kind: z.literal("UPGRADE"), tariffId: idSchema, monthlyDialogs: positiveCountSchema }).strict(),
]);
export const billingPreviewInputSchema = z.object({
    serviceReason: z.string().trim().min(1).max(4000).superRefine((value, context) => {
        if ([...value].some((character) => {
            const code = character.codePointAt(0);
            return code !== undefined && (code <= 0x1f || code === 0x7f);
        })) {
            context.addIssue({ code: z.ZodIssueCode.invalid_string, validation: "regex", message: "Invalid" });
        }
    }).optional(),
    action: billingActionInputSchema,
    expectedCatalogVersion: versionSchema,
    expectedBillingVersion: versionSchema,
}).strict();
const quotedActionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("TOPUP"), dialogs: positiveCountSchema }).strict(),
    z.object({ kind: z.literal("UPGRADE"), tariffId: idSchema, monthlyDialogs: positiveCountSchema }).strict(),
]);
export const billingQuoteInputSchema = z.object({
    action: quotedActionSchema,
    expectedCatalogVersion: versionSchema,
    expectedBillingVersion: versionSchema,
}).strict();
export const billingPriceBreakdownSchema = z.object({
    baseMonthlyRub: nonnegativeDecimalSchema,
    undiscountedRub: nonnegativeDecimalSchema.nullable(),
    discountRub: nonnegativeDecimalSchema.nullable(),
    totalRub: integerRubSchema,
    oldBaseMonthlyRub: nonnegativeDecimalSchema.nullable(),
    remainingMs: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable(),
    periodMs: z.literal(PERIOD_MS),
}).strict();
export const billingQuoteSchema = z.object({
    action: billingActionInputSchema,
    accountId: idSchema,
    catalogVersion: versionSchema,
    billingVersion: versionSchema,
    subscriptionId: idSchema,
    periodId: idSchema,
    calculatedAt: isoUtcSchema,
    expiresAt: isoUtcSchema,
    breakdown: billingPriceBreakdownSchema,
}).strict();
export const billingPreviewSchema = billingQuoteSchema.extend({ previewToken: z.string().min(1), unappliedInvoiceIds: z.array(z.string().uuid()).optional() }).strict();
export const billingConfirmInputSchema = z.object({
    requestId: idSchema,
    previewToken: z.string().min(1),
    expectedBillingVersion: versionSchema,
}).strict();
export const billingOperationResultSchema = z.object({
    id: idSchema,
    requestId: idSchema,
    kind: z.enum(["ASSIGN", "TOPUP", "UPGRADE"]),
    effectiveAt: isoUtcSchema,
    totalRub: integerRubSchema,
    snapshot: paidSubscriptionSnapshotSchema,
}).strict();
export const billingConfirmResultSchema = z.object({
    operation: billingOperationResultSchema,
    billing: adminBillingViewSchema,
}).strict();
export const trialActivateResultSchema = z.object({
    activation: trialActivationSchema, billing: billingViewSchema,
}).strict();
//# sourceMappingURL=tariffs.js.map